import { Injectable } from '@angular/core';
import { User } from '@datadog/browser-core';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { BehaviorSubject } from 'rxjs';
import { ConfigService } from '../config';

@Injectable({ providedIn: 'root' })
export class LoggerService {
  constructor(private readonly configService: ConfigService) { }

  loggingInitialized$ = new BehaviorSubject<boolean>(false);
  sessionReplayInitialized$ = new BehaviorSubject<boolean>(false);

  get loggingInitialized() {
    return this.loggingInitialized$.value;
  }

  get sessionReplayInitialized() {
    return this.sessionReplayInitialized$.value;
  }

  setUser(user: User) {
    datadogLogs.setUser(user);
    datadogRum.setUser(user);
  }

  clearUser() {
    datadogLogs.clearUser();
    datadogRum.clearUser();
  }

  initializeLogging() {
    if (this.loggingInitialized) {
      return;
    }
    datadogLogs.init({
      clientToken: 'pubb2ea7030c8b07e223fcd42225d4ddaf3',
      site: 'us3.datadoghq.com',
      service: 'linq-erp',
      sessionSampleRate: 100,
      env: this.configService.get('environment'),
      version: process.env['BUILD_ID'],
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['debug', 'error'],
    });

    datadogLogs.onReady(() => {
      this.loggingInitialized$.next(true);
    });
  }

  initializeSessionReplay() {
    if (this.sessionReplayInitialized) {
      return;
    }
    datadogRum.init({
      applicationId: 'c846da8a-adb6-4923-b2f2-708f1ab11db2',
      clientToken: 'pubb2ea7030c8b07e223fcd42225d4ddaf3',
      site: 'us3.datadoghq.com',
      service: 'linq-erp',
      env: this.configService.get('environment'),
      version: process.env['BUILD_ID'],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
    });

    datadogRum.onReady(() => {
      this.sessionReplayInitialized$.next(true);
    });
  }

  startRecording() {
    if (!this.sessionReplayInitialized) {
      this.initializeSessionReplay();
    }
    datadogRum.startSessionReplayRecording();
  }

  stopRecording() {
    datadogRum.stopSessionReplayRecording();
  }

  log(message: string) {
    datadogLogs.logger.log(message);
  }

  warn(message: string) {
    datadogLogs.logger.warn(message);
  }

  error(message: string) {
    datadogLogs.logger.error(message);
  }

  debug(message: string) {
    datadogLogs.logger.debug(message);
  }
}
